import { useEffect, useRef } from 'react'

/**
 * The keyboard and focus contract every surface that opens over or beside the
 * page owes its users, in one place so the next one cannot implement half of it.
 *
 * Three obligations, and they are easy to forget separately:
 *  - **Escape closes.** A surface with no keyboard exit is a trap.
 *  - **Focus moves in.** Otherwise focus is still behind the surface, and a
 *    keyboard user has to tab through the whole page to reach what just opened.
 *  - **Focus is restored** to whatever opened it, so dismissing returns the user
 *    where they were rather than at the top of the document.
 *
 * `trapFocus` is the fourth, and only for a *modal* surface: it keeps Tab inside
 * the surface, which is what `aria-modal="true"` promises assistive technology.
 * A non-modal surface (an inline pane beside a list) must deliberately *not*
 * trap — the page behind it is still legitimately usable, which is the whole
 * reason for choosing inline.
 *
 * Lives in `ui-kit/` because it is presentation behaviour with no state of its
 * own; it holds a ref to a DOM node and nothing else.
 */
export function useDismissableSurface({
  active,
  onDismiss,
  trapFocus,
}: {
  active: boolean
  onDismiss: () => void
  trapFocus: boolean
}) {
  const surfaceRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!active) return

    // Captured before focus moves, so dismissal can put it back.
    const previouslyFocused = document.activeElement as HTMLElement | null

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const focusable = (within?: Element | null): HTMLElement[] =>
      Array.from((within ?? surfaceRef.current)?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])

    /**
     * Prefer the first control in `[data-focus-scope]` — the surface's *content* —
     * over the first control in its chrome. A pane holding a rename form should
     * land in the name field so the user can type; landing on the close button
     * means their first keystroke does nothing.
     *
     * Falls back to anything focusable, then to the surface itself: a panel still
     * loading its contents must not leave focus behind the page.
     */
    const scope = surfaceRef.current?.querySelector('[data-focus-scope]')
    const preferred = focusable(scope)[0] ?? focusable()[0]
    ;(preferred ?? surfaceRef.current)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onDismiss()
        return
      }

      if (event.key !== 'Tab' || !trapFocus) return

      const elements = focusable()
      if (elements.length === 0) return

      const first = elements[0]!
      const last = elements[elements.length - 1]!
      // Wrap at both ends: without this, Tab walks straight out of a surface
      // that claims to be modal.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    // Captured now, not read in the cleanup: by then the node is detached and
    // `surfaceRef.current` has already been nulled, so the cleanup would have
    // nothing to compare against. A detached node still answers `contains`.
    const node = surfaceRef.current

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)

      // Restore only if focus is still ours to give back. Two ways it is:
      // it is still inside the closing surface, or the surface was removed from
      // under it and the browser dropped focus to <body>. If the user has since
      // clicked something else, yanking them back would be the rude version.
      const focused = document.activeElement
      const ourFocus = node?.contains(focused) === true
      const focusWasLost = focused === null || focused === document.body
      if (ourFocus || focusWasLost) previouslyFocused?.focus()
    }
  }, [active, onDismiss, trapFocus])

  return surfaceRef
}
